import { AxiosResponse, isAxiosError } from 'axios';
import { createLogger, serializeError } from 'logging';

import type {
    CreateSessionForAdminImpersonationRequest,
    ImpersonateUserResponse,
    InitiateSSOLoginRequest,
    InitiateSSOResponse,
    LoginRequest,
    UserSession,
    UserSessionApiDeactivateAllUserSessionsRequest,
    UserSessionApiGetSessionRequest,
    UserSessionApiInitiateSSOLoginRequest,
    UserSessionApiLogoutRequest,
} from '@videoblocks/user-service-client';

import type { ImpersonateUserRequest } from './ImpersonateUserRequest';
import { userSessionApi } from './userServiceConfig';

const logger = createLogger('LoginApi');

const loginUser = async (
    loginRequest: LoginRequest,
    requestIp: string | null,
    referrer?: string,
): Promise<UserSession | null> => {
    const isSsoLogin = 'ssoCode' in loginRequest;
    try {
        const requestWithMetadata = {
            ...loginRequest,
            app: 'storyblocks',
            ...(referrer && { referrer }),
        };

        const result = (await userSessionApi.login(
            { loginRequest: requestWithMetadata },
            requestIp
                ? {
                      headers: { 'x-forwarded-for': requestIp },
                  }
                : undefined,
        )) as AxiosResponse<UserSession>;
        return result.data;
    } catch (e: unknown) {
        if (isAxiosError(e)) {
            if (e.response?.status === 403) {
                logger.warn(
                    { error: serializeError(e), loginType: isSsoLogin ? 'sso' : 'credentials' },
                    'Error logging in.',
                );
                return null;
            }
            if (e?.response?.status === 404) {
                if (isSsoLogin) {
                    logger.error({ error: serializeError(e) }, 'SSO auto-provisioning failed - user not found');
                } else {
                    logger.info('Google Sign On request was valid, but No account found for this email');
                }
                return null;
            }
            if (e?.response?.status === 401) {
                if (isSsoLogin) {
                    logger.error({ error: serializeError(e) }, 'SSO authentication failed - invalid or expired code');
                } else {
                    logger.warn({ error: serializeError(e) }, 'Invalid credentials');
                }
                return null;
            }
        }
        throw e;
    }
};

const logoutUser = async (sessionToken: string): Promise<boolean> => {
    const request: UserSessionApiLogoutRequest = {
        sessionId: sessionToken,
    };

    try {
        await userSessionApi.logout(request);
        return true;
    } catch (e: unknown) {
        if (isAxiosError(e)) {
            if (e.response?.status === 403) {
                logger.warn({ status: e.status, message: e.message }, 'Error logging out.');
                return false;
            }
            if (e?.response?.status === 404) {
                logger.info(`No session found for token ${sessionToken}.`);
                return false;
            }
        }
        throw e;
    }
};

const getUserSession = async (sessionToken: string): Promise<UserSession | null> => {
    // Validate sessionToken is not empty
    if (!sessionToken || sessionToken.trim() === '') {
        logger.warn('getUserSession called with empty sessionToken');
        return null;
    }

    const request: UserSessionApiGetSessionRequest = {
        sessionId: sessionToken,
    };
    try {
        const result = (await userSessionApi.getSession(request)) as AxiosResponse<UserSession>;
        return result.data;
    } catch (e: unknown) {
        if (isAxiosError(e)) {
            if (e.response?.status !== 403) {
                logger.warn({ status: e.status, message: e.message }, 'Error getting user from session.');
            }
            if (e.response?.status === 404) {
                return null;
            }
        }
        throw e;
    }
};

const deactivateAllDevices = async (userId: number): Promise<boolean> => {
    const request: UserSessionApiDeactivateAllUserSessionsRequest = {
        userId: userId,
    };
    try {
        const result = (await userSessionApi.deactivateAllUserSessions(request)) as AxiosResponse<unknown>;
        return result.status === 200;
    } catch (e: unknown) {
        if (isAxiosError(e)) {
            if (e.response?.status == 400) {
                logger.warn({ status: e.status }, 'Invalid user ID');
            } else if (e.response?.status == 404) {
                logger.warn({ status: e.status }, 'User not found');
            } else {
                logger.warn({ status: e.status, message: e.message }, 'Error logging out devices.');
            }
            return false;
        }
        throw e;
    }
};

const impersonateUser = async (request: ImpersonateUserRequest): Promise<ImpersonateUserResponse> => {
    logger.info(
        { userSessionId: request.userSessionId, adminUserId: request.adminUserId },
        'Requesting impersonation session',
    );

    const impersonationRequest: CreateSessionForAdminImpersonationRequest = {
        userId: request.userSessionId,
        adminUserId: request.adminUserId,
    };

    const result = (await userSessionApi.createSessionForAdminImpersonation({
        createSessionForAdminImpersonationRequest: impersonationRequest,
    })) as AxiosResponse<ImpersonateUserResponse>;

    logger.info(
        {
            userSessionId: result.data.userSessionId,
            userId: request.userSessionId,
            adminUserId: request.adminUserId,
            newUserSessionId: result.data.userSessionId,
        },
        'Impersonation session created successfully',
    );

    return result.data;
};

const getSSODomains = async (): Promise<string[]> => {
    try {
        logger.info({}, 'Calling user-service to get all SSO domains');
        const result = (await userSessionApi.getSsoDomains()) as AxiosResponse<string[]>;
        logger.info({ domainCount: result.data.length }, 'Retrieved SSO domains from user-service');
        return result.data;
    } catch (e: unknown) {
        if (isAxiosError(e)) {
            // Gracefully degrade for API errors - SSO will be disabled
            logger.error({ error: serializeError(e) }, 'API error getting SSO domains, returning empty array');
            return [];
        }
        // Throw unexpected errors (programming errors, etc.)
        logger.error({ error: serializeError(e) }, 'Unexpected error getting SSO domains');
        throw e;
    }
};

const initiateSSOLogin = async (email: string, redirectUrl: string): Promise<string> => {
    const requestBody: InitiateSSOLoginRequest = {
        email,
        redirectUrl,
    };

    const request: UserSessionApiInitiateSSOLoginRequest = {
        initiateSSOLoginRequest: requestBody,
    };

    try {
        const result = (await userSessionApi.initiateSSOLogin(request)) as AxiosResponse<InitiateSSOResponse>;
        logger.info({ email, redirectUrl }, 'SSO login initiated successfully');
        return result.data.authorizationUrl;
    } catch (e: unknown) {
        logger.error({ error: serializeError(e), email, redirectUrl }, 'Error initiating SSO login');
        // Re-throw Error instances as-is, wrap non-Error objects (like some Axios errors)
        // to ensure proper promise rejection behavior
        if (e instanceof Error) {
            throw e;
        }
        throw new Error('Failed to initiate SSO login');
    }
};

export const UserSessionApi = {
    loginUser,
    logoutUser,
    getUserSession,
    deactivateAllDevices,
    impersonateUser,
    getSSODomains,
    initiateSSOLogin,
};
